"use client";
// Imports
import { useState, useEffect } from "react";
import { ComponentWrapper, Countdown, Img } from "@/components";
import { HeroCtaBar } from "./HeroCtaBar";
import { Button } from "@/components/ui/button";
import { cva } from "class-variance-authority";
import Link from "next/link";
import { resolveLink, cn } from "@/lib/utils";
import { formatShowDateRangeLabel } from "@/lib/showDates";

const SLIDE_INTERVAL_MS = 5000;
const FADE_DURATION_MS = 1000;
function formatOverlayDateFallback(unformattedStartDate, unformattedEndDate) {
  return formatShowDateRangeLabel(
    unformattedStartDate,
    unformattedEndDate
  ).toUpperCase();
}

function resolveButtonVariant(style) {
  const value = typeof style === "object" ? style?.value : style;
  if (value === "outline") return "outline";
  return "default";
}

function slideHasMobileAlternate(slide) {
  const mobilePermalink = slide?.mobile_image?.permalink;
  return Boolean(
    mobilePermalink && mobilePermalink !== slide?.image?.permalink
  );
}

function HeroSlideLayers({ slide, priority = false }) {
  const hasMobileAlternate = slideHasMobileAlternate(slide);

  return (
    <div className="absolute inset-0">
      {hasMobileAlternate && (
        <div className="absolute inset-0 lg:hidden">
          <Img
            data={slide.mobile_image}
            fill
            sizes="100vw"
            priority={priority}
            className="object-cover"
          />
        </div>
      )}
      <div
        className={cn(
          "absolute inset-0",
          hasMobileAlternate ? "hidden lg:block" : ""
        )}
      >
        {slide?.image && (
          <Img
            data={slide.image}
            fill
            sizes="100vw"
            priority={priority}
            className="object-cover"
          />
        )}
      </div>
    </div>
  );
}

const textOverlayStyles = cva(
  "pointer-events-none absolute z-[2] w-full max-w-full lg:w-1/2 flex flex-col p-6 lg:p-16",
  {
    variants: {
      overlayAlignment: {
        "top-left": "top-0 left-0 text-left items-start",
        "top-center":
          "top-0 left-1/2 -translate-x-1/2 text-center items-center",
        "top-right": "top-0 right-0 text-right items-end",
        "middle-left":
          "top-1/2 left-0 -translate-y-1/2 text-left items-start",
        center:
          "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center items-center",
        "middle-right":
          "top-1/2 right-0 -translate-y-1/2 text-right items-end",
        "bottom-left": "bottom-0 left-0 text-left items-start",
        "bottom-center":
          "bottom-0 left-1/2 -translate-x-1/2 text-center items-center",
        "bottom-right": "bottom-0 right-0 text-right items-end",
      },
    },
    defaultVariants: {
      overlayAlignment: "middle-left",
    },
  }
);

// Hero Slideshow
export function HeroSlideshow({
  showCount,
  type,
  slides,
  showTextOverlay,
  textOverlayAlignment,
  overlayDate,
  title,
  caption,
  buttonTitle,
  link,
  buttonStyle,
  useCustomColours,
  customButtonBackgroundColor,
  customButtonForegroundColor,
  daysUntil,
  startDate,
  endDate,
  showStartDate,
  showStartTime,
  unformattedStartDate,
  unformattedEndDate,
  showCtaBar = false,
  ctaBadge,
  ctaTitle,
  ctaText,
  ctaButtonTitle,
  ctaLink,
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const useLightOverlay = Boolean(showCtaBar);

  // Lock aspect to the first slide so carousel changes don't shift the page.
  const sizingSlide = slides?.[0];
  const desktopAsset = sizingSlide?.image;
  const mobileAsset = sizingSlide?.mobile_image || desktopAsset;
  const hasMobileAlternate = slideHasMobileAlternate(sizingSlide);
  const desktopPaddingBottom =
    desktopAsset?.width && desktopAsset?.height
      ? `${(desktopAsset.height / desktopAsset.width) * 100}%`
      : `${(9 / 16) * 100}%`;
  const mobilePaddingBottom =
    mobileAsset?.width && mobileAsset?.height
      ? `${(mobileAsset.height / mobileAsset.width) * 100}%`
      : desktopPaddingBottom;

  useEffect(() => {
    if (!slides?.length || slides.length < 2) return;

    const timer = setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, SLIDE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [slides?.length]);

  const resolvedDate =
    overlayDate?.trim() ||
    formatOverlayDateFallback(unformattedStartDate, unformattedEndDate);
  const buttonHref = resolveLink(link);
  // Match header primary button treatment; default to red gum (#892102)
  const customColors = {
    background:
      (useCustomColours && customButtonBackgroundColor) || "#892102",
    foreground:
      (useCustomColours && customButtonForegroundColor) || "#FFFFFF",
  };

  // Render
  return (
    <ComponentWrapper type={type}>
      <div className="relative w-full" style={{ width: "100%" }}>
        {/*
          In-flow percentage padding reserves height before CSS/images load.
          Abspos-only frames collapse to 0×0 until Tailwind `w-full` applies.
        */}
        {hasMobileAlternate && (
          <style>{`
            [data-hero-spacer="${sizingSlide?.id ?? "hero"}"] {
              padding-bottom: ${mobilePaddingBottom};
            }
            @media (min-width: 1024px) {
              [data-hero-spacer="${sizingSlide?.id ?? "hero"}"] {
                padding-bottom: ${desktopPaddingBottom};
              }
            }
          `}</style>
        )}
        <div
          className="relative w-full overflow-hidden"
          style={{ width: "100%", position: "relative" }}
        >
          <div
            data-hero-spacer={sizingSlide?.id ?? "hero"}
            aria-hidden
            style={{
              width: "100%",
              paddingBottom: hasMobileAlternate
                ? undefined
                : desktopPaddingBottom,
            }}
          />
          {slides.map((slide, index) => (
            <div
              key={slide?.id ?? index}
              className={cn(
                "absolute inset-0 transition-opacity ease-in-out",
                index === activeIndex ? "opacity-100 z-[1]" : "opacity-0 z-0"
              )}
              style={{ transitionDuration: `${FADE_DURATION_MS}ms` }}
              aria-hidden={index !== activeIndex}
            >
              <HeroSlideLayers slide={slide} priority={index === 0} />
            </div>
          ))}

          {useLightOverlay && (
            <div
              className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-t from-black/70 via-black/40 to-black/25"
              aria-hidden
            />
          )}

          {showTextOverlay &&
            (resolvedDate || title || caption || buttonHref) && (
              <div
                className={textOverlayStyles({
                  overlayAlignment: textOverlayAlignment?.value,
                })}
              >
                {resolvedDate && (
                  <p className="font-heading text-brand-red-gum text-md lg:text-lg tracking-wide mb-1.5 lg:mb-2 w-full">
                    {resolvedDate}
                  </p>
                )}
                {(title || caption) && (
                  <div className="w-full lg:w-max lg:max-w-full">
                    {title && (
                      <h2
                        className={cn(
                          "font-heading tracking-tight text-[clamp(1.75rem,8.5vw,3rem)] lg:text-[4.5rem]",
                          "flex flex-col leading-[0.88]",
                          useLightOverlay
                            ? "text-brand-wool"
                            : "text-brand-espresso"
                        )}
                      >
                        {title.split("\n").map((line, index) => (
                          <span
                            key={`${line}-${index}`}
                            className="block max-w-full whitespace-normal sm:whitespace-nowrap leading-[0.88]"
                          >
                            {line}
                          </span>
                        ))}
                      </h2>
                    )}
                    {caption && (
                      <p
                        className={cn(
                          "font-body text-sm sm:text-base lg:text-lg leading-snug mt-4 lg:mt-5 w-full lg:w-0 lg:min-w-full",
                          useLightOverlay
                            ? "text-brand-wool/90"
                            : "text-brand-espresso"
                        )}
                      >
                        {caption}
                      </p>
                    )}
                  </div>
                )}
                {buttonHref && buttonTitle && (
                  <div className="pointer-events-auto pt-5 lg:pt-6">
                    <Button
                      variant={resolveButtonVariant(buttonStyle)}
                      customColors={customColors}
                      className="font-heading !uppercase tracking-normal leading-normal h-11 px-6"
                      asChild
                    >
                      <Link href={buttonHref}>{buttonTitle}</Link>
                    </Button>
                  </div>
                )}
              </div>
            )}
        </div>

        {showCtaBar ? (
          // Pull CTA up 1px to cover the subpixel seam above the bar
          <div className="relative z-[2] -mt-px">
            <HeroCtaBar
              badge={ctaBadge}
              title={ctaTitle}
              text={ctaText}
              buttonTitle={ctaButtonTitle}
              link={ctaLink}
            />
          </div>
        ) : (
          <Countdown
            showCount={showCount}
            unformattedStartDate={unformattedStartDate}
            unformattedEndDate={unformattedEndDate}
            daysUntil={daysUntil}
            startDate={startDate}
            endDate={endDate}
            showStartDate={showStartDate}
            showStartTime={showStartTime}
          />
        )}
      </div>
    </ComponentWrapper>
  );
}
